const CONSOLE_LOGIN_API_URL =
	process.env.NEXT_PUBLIC_CONSOLE_LOGIN_API_URL ??
	'https://api.onekyc.io/admin/v1/auth/login';
const CONSOLE_WEBAUTHN_LOGIN_OPTIONS_API_URL =
	process.env.NEXT_PUBLIC_CONSOLE_WEBAUTHN_LOGIN_OPTIONS_API_URL ??
	'https://api.onekyc.io/admin/v1/auth/webauthn/login/options';
const CONSOLE_WEBAUTHN_LOGIN_VERIFY_API_URL =
	process.env.NEXT_PUBLIC_CONSOLE_WEBAUTHN_LOGIN_VERIFY_API_URL ??
	'https://api.onekyc.io/admin/v1/auth/webauthn/login/verify';

export const CONSOLE_HOME_URL =
	process.env.NEXT_PUBLIC_CONSOLE_HOME_URL ?? 'https://console.onekyc.io/';

interface ConsoleLoginInput {
	email: string;
	password: string;
	totp?: string;
}

interface LoginEnvelope {
	success?: boolean;
	error?: {
		code?: string;
		message?: string;
	};
	data?: unknown;
}

interface WebAuthnLoginOptionsEnvelope {
	success?: boolean;
	error?: {
		code?: string;
		message?: string;
	};
	data?: {
		challenge_id?: string;
		public_key?: PublicKeyCredentialRequestOptionsJSON;
	};
}

interface PublicKeyCredentialRequestOptionsJSON {
	challenge: string;
	rpId?: string;
	timeout?: number;
	userVerification?: UserVerificationRequirement;
	allowCredentials?: PublicKeyCredentialDescriptorJSON[];
}

interface PublicKeyCredentialDescriptorJSON {
	id: string;
	type: PublicKeyCredentialType;
	transports?: AuthenticatorTransport[];
}

interface AuthenticatorAssertionResponseJSON {
	authenticatorData: string;
	clientDataJSON: string;
	signature: string;
	userHandle?: string | null;
}

interface PublicKeyCredentialAssertionJSON {
	id: string;
	rawId: string;
	type: PublicKeyCredentialType;
	response: AuthenticatorAssertionResponseJSON;
	clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

interface PasskeyLoginOptions {
	mediation?: CredentialMediationRequirement;
	signal?: AbortSignal;
}

export class ConsoleLoginError extends Error {
	readonly code: string;
	readonly status: number;

	constructor(status: number, code: string, message: string) {
		super(message);
		this.name = 'ConsoleLoginError';
		this.status = status;
		this.code = code;
	}
}

async function readLoginEnvelope(response: Response): Promise<LoginEnvelope> {
	try {
		const envelope = (await response.json()) as unknown;
		if (!envelope || typeof envelope !== 'object') {
			return {};
		}
		return envelope as LoginEnvelope;
	} catch {
		return {};
	}
}

async function readWebAuthnLoginOptionsEnvelope(
	response: Response,
): Promise<WebAuthnLoginOptionsEnvelope> {
	try {
		const envelope = (await response.json()) as unknown;
		if (!envelope || typeof envelope !== 'object') {
			return {};
		}
		return envelope as WebAuthnLoginOptionsEnvelope;
	} catch {
		return {};
	}
}

function base64URLToBuffer(value: string): ArrayBuffer {
	const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
	const padding = (4 - (base64.length % 4)) % 4;
	const padded = base64 + '='.repeat(padding);
	const binary = atob(padded);
	const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
	return bytes.buffer;
}

function bufferToBase64URL(value: ArrayBuffer): string {
	const bytes = new Uint8Array(value);
	let binary = '';
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	return btoa(binary)
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/u, '');
}

function toCredentialRequestOptions(
	options: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
	return {
		...options,
		challenge: base64URLToBuffer(options.challenge),
		allowCredentials: options.allowCredentials?.map((credential) => ({
			...credential,
			id: base64URLToBuffer(credential.id),
		})),
	};
}

export async function isPasskeyConditionalMediationAvailable(): Promise<boolean> {
	const credentialApi = globalThis.PublicKeyCredential as
		| (typeof PublicKeyCredential & {
				isConditionalMediationAvailable?: () => Promise<boolean>;
		  })
		| undefined;
	return typeof credentialApi?.isConditionalMediationAvailable === 'function'
		? credentialApi.isConditionalMediationAvailable()
		: false;
}

function isPublicKeyCredentialLike(
	credential: Credential | null,
): credential is PublicKeyCredential {
	const maybePublicKey = credential as
		| (Credential & { getClientExtensionResults?: unknown })
		| null;
	return (
		maybePublicKey != null &&
		maybePublicKey.type === 'public-key' &&
		'rawId' in maybePublicKey &&
		'response' in maybePublicKey &&
		typeof maybePublicKey.getClientExtensionResults === 'function'
	);
}

function isAssertionResponseLike(
	response: AuthenticatorResponse,
): response is AuthenticatorAssertionResponse {
	return 'authenticatorData' in response && 'signature' in response;
}

function assertionToJSON(
	credential: PublicKeyCredential,
): PublicKeyCredentialAssertionJSON {
	const response = credential.response;
	if (!isAssertionResponseLike(response)) {
		throw new ConsoleLoginError(0, 'passkey_failed', 'Passkey sign-in failed');
	}
	return {
		id: credential.id,
		rawId: bufferToBase64URL(credential.rawId),
		type: 'public-key',
		response: {
			authenticatorData: bufferToBase64URL(response.authenticatorData),
			clientDataJSON: bufferToBase64URL(response.clientDataJSON),
			signature: bufferToBase64URL(response.signature),
			userHandle: response.userHandle
				? bufferToBase64URL(response.userHandle)
				: null,
		},
		clientExtensionResults: credential.getClientExtensionResults(),
	};
}

export async function loginToConsole(input: ConsoleLoginInput): Promise<{
	status: 'success';
	redirectTo: string;
}> {
	const body: ConsoleLoginInput = {
		email: input.email,
		password: input.password,
	};
	if (input.totp) {
		body.totp = input.totp;
	}

	let response: Response;
	try {
		response = await fetch(CONSOLE_LOGIN_API_URL, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
	} catch {
		throw new ConsoleLoginError(0, 'network_error', 'Login failed');
	}
	const envelope = await readLoginEnvelope(response);

	if (!response.ok || envelope.success !== true) {
		throw new ConsoleLoginError(
			response.status,
			envelope.error?.code ?? 'login_failed',
			envelope.error?.message ?? 'Login failed',
		);
	}

	return { status: 'success', redirectTo: CONSOLE_HOME_URL };
}

export async function loginToConsoleWithPasskey(
	options: PasskeyLoginOptions = {},
): Promise<{
	status: 'success';
	redirectTo: string;
}> {
	if (
		!('credentials' in navigator) ||
		typeof navigator.credentials.get !== 'function'
	) {
		throw new ConsoleLoginError(
			0,
			'passkey_unsupported',
			'Passkey sign-in is not supported',
		);
	}

	let optionsResponse: Response;
	try {
		optionsResponse = await fetch(CONSOLE_WEBAUTHN_LOGIN_OPTIONS_API_URL, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({}),
		});
	} catch {
		throw new ConsoleLoginError(0, 'network_error', 'Login failed');
	}
	const optionsEnvelope =
		await readWebAuthnLoginOptionsEnvelope(optionsResponse);
	if (!optionsResponse.ok || optionsEnvelope.success !== true) {
		throw new ConsoleLoginError(
			optionsResponse.status,
			optionsEnvelope.error?.code ?? 'passkey_failed',
			optionsEnvelope.error?.message ?? 'Passkey sign-in failed',
		);
	}
	const challengeID = optionsEnvelope.data?.challenge_id;
	const publicKey = optionsEnvelope.data?.public_key;
	if (!challengeID || !publicKey) {
		throw new ConsoleLoginError(
			optionsResponse.status,
			'passkey_failed',
			'Passkey sign-in failed',
		);
	}

	let credential: Credential | null;
	try {
		const credentialRequest: CredentialRequestOptions = {
			publicKey: toCredentialRequestOptions(publicKey),
		};
		if (options.mediation !== undefined) {
			credentialRequest.mediation = options.mediation;
		}
		if (options.signal !== undefined) {
			credentialRequest.signal = options.signal;
		}
		credential = await navigator.credentials.get(credentialRequest);
	} catch {
		throw new ConsoleLoginError(
			0,
			'passkey_cancelled',
			'Passkey sign-in cancelled',
		);
	}
	if (!isPublicKeyCredentialLike(credential)) {
		throw new ConsoleLoginError(0, 'passkey_failed', 'Passkey sign-in failed');
	}

	let verifyResponse: Response;
	try {
		verifyResponse = await fetch(CONSOLE_WEBAUTHN_LOGIN_VERIFY_API_URL, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				challenge_id: challengeID,
				assertion: assertionToJSON(credential),
			}),
		});
	} catch {
		throw new ConsoleLoginError(0, 'network_error', 'Login failed');
	}
	const verifyEnvelope = await readLoginEnvelope(verifyResponse);
	if (!verifyResponse.ok || verifyEnvelope.success !== true) {
		throw new ConsoleLoginError(
			verifyResponse.status,
			verifyEnvelope.error?.code ?? 'passkey_failed',
			verifyEnvelope.error?.message ?? 'Passkey sign-in failed',
		);
	}

	return { status: 'success', redirectTo: CONSOLE_HOME_URL };
}
